import { createMemo, createSignal, For, onCleanup, Show } from 'solid-js'
import { vec2f, vec4f } from 'typegpu/data'
import { STATIC_PREVIEW_POINT_COUNT, THUMBNAIL_PREVIEW_QUALITY, } from '@/defaults'
import { examples } from '@/flame/examples'
import { Flam3 } from '@/flame/Flam3'
import { Cross } from '@/icons'
import { AutoCanvas } from '@/lib/AutoCanvas'
import { Camera2D } from '@/lib/Camera2D'
import { Default3DPreviewCamera } from '@/lib/Camera3D'
import { Root } from '@/lib/Root'
import { deepClone } from '@/utils/clone'
import { formatRecentDate, loadRecentFlames } from '@/utils/recentFlames'
import { recordEntries } from '@/utils/record'
import { DelayedShow } from '../DelayedShow/DelayedShow'
import ui from './BlendFlameGallery.module.css'
import type { FlameDescriptor } from '@/flame/schema/flameSchema'

type BlendFlameGalleryProps = {
  onSelect: (flame: FlameDescriptor) => void
  onPreviewBlend?: (flame: FlameDescriptor | null) => void
  onPreviewName?: (name: string | null) => void
  onClose: () => void
  /** Header label — defaults to the blend wording. */
  heading?: string
}

const INITIAL_VISIBLE = 10
const PREVIEW_CLEAR_DELAY = 120

function Preview(props: { flameDescriptor: FlameDescriptor }) {
  return (
    <Root adapterOptions={{ powerPreference: 'high-performance' }}>
      <AutoCanvas pixelRatio={1}>
        {(() => {
          const flameView = () => (
            <Flam3
              quality={THUMBNAIL_PREVIEW_QUALITY}
              pointCountPerBatch={STATIC_PREVIEW_POINT_COUNT}
              adaptiveFilterEnabled={false}
              animationEnabled={false}
              flameDescriptor={props.flameDescriptor}
              renderInterval={1}
              onExportImage={undefined}
              edgeFadeColor={vec4f(0)}
              onAccumulatedPointCount={() => {}}
            />
          )
          return (
            <Show
              when={
                (props.flameDescriptor.renderSettings.dimensions ?? 2) === 3
              }
              fallback={
                <Camera2D
                  position={vec2f(
                    ...props.flameDescriptor.renderSettings.camera.position,
                  )}
                  zoom={props.flameDescriptor.renderSettings.camera.zoom}
                >
                  {flameView()}
                </Camera2D>
              }
            >
              <Default3DPreviewCamera>{flameView()}</Default3DPreviewCamera>
            </Show>
          )
        })()}
      </AutoCanvas>
    </Root>
  )
}

export function BlendFlameGallery(props: BlendFlameGalleryProps) {
  const [visibleRecentCount, setVisibleRecentCount] =
    createSignal(INITIAL_VISIBLE)
  const [visibleExamplesCount, setVisibleExamplesCount] =
    createSignal(INITIAL_VISIBLE)
  const [filterText, setFilterText] = createSignal('')

  const allRecent = () =>
    loadRecentFlames().filter(
      (recent) => (recent.flame.renderSettings.dimensions ?? 2) !== 3,
    )

  const allExamples = () =>
    recordEntries(examples)
      .filter(([, flame]) => (flame.renderSettings.dimensions ?? 2) !== 3)
      .map(([id, flame]) => ({
        id,
        name: id === 'initExample' ? 'Init' : id,
        flame,
      }))

  const filteredRecent = createMemo(() => {
    const q = filterText().toLowerCase().trim()
    if (!q) return allRecent()
    return allRecent().filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.flame.metadata?.name?.toLowerCase().includes(q),
    )
  })

  const filteredExamples = createMemo(() => {
    const q = filterText().toLowerCase().trim()
    if (!q) return allExamples()
    return allExamples().filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.flame.metadata?.name?.toLowerCase().includes(q),
    )
  })

  const showAllRecent = () => visibleRecentCount() >= filteredRecent().length
  const showAllExamples = () =>
    visibleExamplesCount() >= filteredExamples().length

  let clearTimer: ReturnType<typeof setTimeout> | undefined

  function handleMouseEnter(flame: FlameDescriptor, name: string) {
    clearTimeout(clearTimer)
    props.onPreviewBlend?.(flame)
    props.onPreviewName?.(name)
  }

  function handleMouseLeave() {
    clearTimer = setTimeout(() => {
      props.onPreviewBlend?.(null)
      props.onPreviewName?.(null)
    }, PREVIEW_CLEAR_DELAY)
  }

  function handleKey(e: KeyboardEvent) {
    if (e.key === 'Escape') props.onClose()
  }

  window.addEventListener('keydown', handleKey)
  onCleanup(() => {
    window.removeEventListener('keydown', handleKey)
    clearTimeout(clearTimer)
  })

  return (
    <div class={ui.container}>
      <div class={ui.header}>
        <span class={ui.title}>{props.heading ?? 'Pick Blend Flame'}</span>
        <button class={ui.closeBtn} onClick={props.onClose} title="Close (Esc)">
          <Cross />
        </button>
      </div>

      <div class={ui.filterRow}>
        <input
          class={ui.filterInput}
          type="text"
          placeholder="Filter by name..."
          value={filterText()}
          onInput={(e) => setFilterText(e.currentTarget.value)}
        />
        <Show when={filterText().length > 0}>
          <button
            class={ui.filterClear}
            onClick={() => setFilterText('')}
            title="Clear filter"
          >
            <Cross />
          </button>
        </Show>
      </div>

      <div class={ui.body}>
        <div>
          <div class={ui.sectionHeader}>
            <span class={ui.sectionLabel}>Recent Flames</span>
          </div>
          <Show
            when={filteredRecent().length > 0}
            fallback={
              <div class={ui.sectionEmpty}>
                {allRecent().length > 0
                  ? 'No matching recent flames'
                  : 'No recent flames yet'}
              </div>
            }
          >
            <div class={ui.grid}>
              <For each={filteredRecent()}>
                {(recent, i) => (
                  <Show when={i() < visibleRecentCount()}>
                    <button
                      class={ui.thumbnail}
                      title={`${recent.name} — ${formatRecentDate(recent.savedAt)}`}
                      onClick={() => {
                        clearTimeout(clearTimer)
                        props.onSelect(deepClone(recent.flame))
                        props.onPreviewName?.(null)
                      }}
                      onMouseEnter={() => {
                        handleMouseEnter(recent.flame, recent.name)
                      }}
                      onMouseLeave={() => {
                        handleMouseLeave()
                      }}
                    >
                      <DelayedShow delayMs={i() * 30}>
                        <Preview flameDescriptor={recent.flame} />
                      </DelayedShow>
                      <div class={ui.thumbnailBar}>
                        <span class={ui.thumbnailName}>{recent.name}</span>
                        <span class={ui.thumbnailMeta}>
                          {recent.savedAt && formatRecentDate(recent.savedAt)}
                        </span>
                      </div>
                    </button>
                  </Show>
                )}
              </For>
            </div>
            <Show when={filteredRecent().length > INITIAL_VISIBLE}>
              <button
                class={ui.showMoreBtn}
                onClick={() =>
                  setVisibleRecentCount((c) =>
                    c >= filteredRecent().length
                      ? INITIAL_VISIBLE
                      : filteredRecent().length,
                  )
                }
              >
                {showAllRecent()
                  ? 'Show less'
                  : `Show more (${filteredRecent().length - INITIAL_VISIBLE} more)`}
              </button>
            </Show>
          </Show>
        </div>

        <div>
          <div class={ui.sectionHeader}>
            <span class={ui.sectionLabel}>Examples</span>
          </div>
          <div class={ui.grid}>
            <For each={filteredExamples()}>
              {({ name, flame }, i) => (
                <Show when={i() < visibleExamplesCount()}>
                  <button
                    class={ui.thumbnail}
                    title={name}
                    onClick={() => {
                      clearTimeout(clearTimer)
                      props.onSelect(deepClone(flame))
                      props.onPreviewName?.(null)
                    }}
                    onMouseEnter={() => {
                      handleMouseEnter(flame, name)
                    }}
                    onMouseLeave={() => {
                      handleMouseLeave()
                    }}
                  >
                    <DelayedShow delayMs={i() * 30}>
                      <Preview flameDescriptor={flame} />
                    </DelayedShow>
                    <div class={ui.thumbnailBar}>
                      <span class={ui.thumbnailName}>{name}</span>
                    </div>
                  </button>
                </Show>
              )}
            </For>
          </div>
          <Show when={filteredExamples().length > INITIAL_VISIBLE}>
            <button
              class={ui.showMoreBtn}
              onClick={() =>
                setVisibleExamplesCount((c) =>
                  c >= filteredExamples().length
                    ? INITIAL_VISIBLE
                    : filteredExamples().length,
                )
              }
            >
              {showAllExamples()
                ? 'Show less'
                : `Show more (${filteredExamples().length - INITIAL_VISIBLE} more)`}
            </button>
          </Show>
        </div>
      </div>
    </div>
  )
}
